import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col
} from 'reactstrap';
import { gql } from 'apollo-boost';
import { Mutation } from 'react-apollo';
import Loader from './Loader';
import { fetchPatientDetails, UPDATE_BOOKING } from '../queries';
import { displaySuccess, displayError } from '../utilities';
import Select from 'react-select';
import MpidModal from './MpidModal';

export const UPDATE_MPID = gql`
  mutation UPDATE_MPID($input: BookingUpdateInput) {
    booking: updateBooking(input: $input) {
      id
      mpid
    }
  }
`;

const UpdateMpidModal = props => {
  const { isOpen, toggle, booking } = props;
  const [mobile, updateMobile] = useState('');
  const [mpid, updateMpid] = useState('');
  const [patientList, setPatientList] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [updateBooking, setUpdateBooking] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    updateMobile(booking && booking.userMobile);
    updateMpid('');
    setPatientList([]);
    setShowConfirmModal(false);
    setUpdateBooking(null);
  }, [isOpen, toggle, booking]);

  const submitMpi = async (updateBooking, skipConfirm: false) => {
    try {
      const selectedPatient = patientList.find(p => p.mpid === mpid);
      if (!mpid || !selectedPatient) {
        displayError('Please select a valid MPID.');
        return;
      }

      if (!skipConfirm) {
        setUpdateBooking(() => updateBooking);
        setShowConfirmModal(true);
        return;
      }

      if (mpid.length === 16 && /^\d+$/.test(mpid)) {
        await updateBooking({
          variables: {
            input: {
              id: booking.bookingId,
              mpid
            }
          }
        });
        displaySuccess('MPID Updated Successfully');
        updateMpid('');
        toggle();
      } else {
        throw new Error('Provide a valid 16-digit MPID.');
      }
    } catch (e) {
      displayError(e.message);
    }
  };

  const gettingPatientDetails = async () => {
    setIsLoading(true);
    try {
      const response = await fetchPatientDetails({
        mobile: mobile || booking.userMobile,
        includeDeleted: false,
        forceSync: true
      });

      const patients = response.data.getPatientDetails || [];

      if (patients.length) {
        setPatientList(patients);
        displaySuccess('MPIDs successfully found for the mobile number');
      } else {
        displayError('No MPIDs found for the mobile number');
        setPatientList([]);
        updateMpid('');
      }
    } catch (error) {
      displayError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const onSuccess = () => {
    setShowConfirmModal(false);
    if (updateBooking) {
      submitMpi(updateBooking, true);
    }
  };

  const onCancel = () => {
    setShowConfirmModal(false);
  };

  if (!booking) return null;

  const selectedMpid = patientList.find(p => p.mpid === mpid);

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={toggle}
        onClosed={() => {}}
        keyboard={false}
        backdrop="static"
        size="lg"
      >
        <ModalHeader toggle={toggle}>
          UPDATE MPID FOR {booking.userName.toUpperCase()} APPOINTMENT WITH{' '}
          {booking.doctorName.toUpperCase()}
        </ModalHeader>
        <ModalBody>
          <Mutation mutation={UPDATE_BOOKING}>
            {(updateBooking, { loading }) => {
              if (loading) return <Loader />;

              return (
                <Row>
                  <Col sm={4} className="mt-2">
                    Search Using Mobile Number
                  </Col>
                  <Col sm={5}>
                    <input
                      type="text"
                      maxLength={10}
                      onKeyPress={e => {
                        if (!/[0-9]/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onChange={e => {
                        const value = e.target.value;
                        updateMobile(value);
                      }}
                      defaultValue={booking.userMobile}
                      className="form-control"
                    />
                  </Col>
                  <Col sm={3}>
                    <Button
                      color="primary"
                      onClick={gettingPatientDetails}
                      disabled={isLoading || !mobile || mobile.length < 10}
                    >
                      Search
                    </Button>
                  </Col>
                  {patientList && patientList.length > 0 ? (
                    <Col sm={12} className="mt-3">
                      <Select
                        value={patientList.find(p => p.mpid === mpid) || null}
                        placeholder="Click to select from available MPIDs"
                        options={patientList}
                        onChange={v => updateMpid(v.mpid)}
                        getOptionLabel={option =>
                          `${option.first_name} ${option.last_name} (MPID: ${option.mpid})`
                        }
                        getOptionValue={option => option.mpid}
                        noOptionsMessage={() =>
                          'No MPIDs found for the number. Please check the number'
                        }
                      />
                    </Col>
                  ) : null}

                  {mpid && (patientList && patientList.length > 0) && (
                    <Col className="d-flex justify-content-center mt-4">
                      <Button
                        color="success"
                        onClick={() => submitMpi(updateBooking)}
                      >
                        Submit
                      </Button>
                    </Col>
                  )}
                </Row>
              );
            }}
          </Mutation>
        </ModalBody>
        <ModalFooter />
      </Modal>

      <MpidModal
        showConfirmModal={showConfirmModal}
        setShowConfirmModal={setShowConfirmModal}
        onSuccess={onSuccess}
        onCancel={onCancel}
        booking={booking}
        selectedMpid={selectedMpid ? [selectedMpid] : []}
      />
    </>
  );
};

UpdateMpidModal.propTypes = {
  isOpen: PropTypes.bool,
  toggle: PropTypes.func,
  booking: PropTypes.object
};

export default UpdateMpidModal;
